import { Component, OnInit, inject } from "@angular/core";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { TabService } from "@services/tabs/TabService";
import { Pages } from "@constants/pages/Pages";
import { ModuleType } from "@enums/ModuleType";
import { WorkspacesService } from "@app/workspaces/services/workspaces-data.service";

import { WorkspaceModulePermissionModel } from "@models/permissions/WorkspaceModulePermissions";
import { TranslocoService } from "@jsverse/transloco";
import { nameof } from "@methods/CommonMethods";
import { AssetOverviewModel } from "@app/workspaces/models/assetoverviewmodel";
import { firstValueFrom, Observable } from "rxjs";

@Component({
    selector: "app-assetmanagement-overview",
    templateUrl: "./assetmanagement-overview.component.html",
    styleUrls: ["./assetmanagement-overview.component.scss"],
    // eslint-disable-next-line
    standalone: false,
})
export class AssetManagementOverviewComponent implements OnInit {
    moduleType = ModuleType.AssetManagement;
    tabID: string;
    tab: TabModel;
    data: Promise<AssetOverviewModel[]>;
    headerLookup: { [propName: string]: string } = {};
    numberProps: string[] = [nameof<AssetOverviewModel>("reference")];
    dateProps: string[] = [
        nameof<AssetOverviewModel>("dateInUse"),
        nameof<AssetOverviewModel>("dateOutOfUse"),
    ];

    permissions: WorkspaceModulePermissionModel;

    private tabService = inject(TabService);
    private workspaceService = inject(WorkspacesService);
    private pages = inject(Pages);
    private translocoService = inject(TranslocoService);

    // Started on first use and cached, so every synchronous translate() call below can await
    // the same load instead of racing the lazy scope. Transloco caches the result, so this
    // resolves immediately once the scope has loaded.
    private scopeLoaded$?: Observable<unknown>;
    private loadScope(): Observable<unknown> {
        return (this.scopeLoaded$ ??= this.translocoService.load(
            `assetmanagement/${this.translocoService.getActiveLang()}`,
        ));
    }

    async ngOnInit() {
        // temp
        this.permissions = new WorkspaceModulePermissionModel();
        this.permissions.canAdd = true;
        this.permissions.canOpen = true;

        this.data = this.loadData();
        await this.loadHeaderTranslations();
    }

    // Merged so the outcome is independent of whether this or mapHeaders() (which fills the
    // remaining, untranslated columns) finishes first.
    private async loadHeaderTranslations() {
        await firstValueFrom(this.loadScope());
        this.headerLookup = { ...this.headerLookup, ...this.getHeaderLookup() };
    }

    public open(asset: AssetOverviewModel) {
        this.tab.showLoader = true;
        this.tabService.generateTab(
            this.pages.AssetManagementView,
            asset.guid,
            this.translocoService.translate("assetmanagement.overview.assetTabTitle", {
                reference: asset.reference,
                name: asset.name,
            }),
            null,
        );
        this.tab.showLoader = false;
    }

    public async add() {
        await firstValueFrom(this.loadScope());
        this.tabService.generateTab(
            this.pages.AssetManagementRegister,
            null,
            this.translocoService.translate("assetmanagement.overview.addNewAssetTab"),
        );
    }

    private async loadData() {
        const data = await firstValueFrom(this.workspaceService.getAssetWorkspaceVbeta());
        return this.mapHeaders((data ?? []).map(AssetOverviewModel.fromApi));
    }

    private mapHeaders = (rows: AssetOverviewModel[]) => {
        if (rows && rows.length) {
            Object.getOwnPropertyNames(rows[0]).forEach((propName) => {
                if (!this.headerLookup[propName]) {
                    this.headerLookup[propName] = propName;
                }
            });
        }
        return rows;
    };

    private getHeaderLookup(): { [propName: string]: string } {
        const headerLookup: { [propName: string]: string } = {};
        headerLookup[nameof<AssetOverviewModel>("reference")] = this.translocoService.translate(
            "assetmanagement.overview.referenceHeader",
        );
        headerLookup[nameof<AssetOverviewModel>("name")] = this.translocoService.translate(
            "assetmanagement.overview.nameHeader",
        );
        headerLookup[nameof<AssetOverviewModel>("organizationName")] =
            this.translocoService.translate("assetmanagement.overview.organizationNameHeader");

        headerLookup[nameof<AssetOverviewModel>("userName")] = this.translocoService.translate(
            "assetmanagement.overview.userNameHeader",
        );
        headerLookup[nameof<AssetOverviewModel>("description")] = this.translocoService.translate(
            "assetmanagement.overview.descriptionHeader",
        );
        headerLookup[nameof<AssetOverviewModel>("dateInUse")] = this.translocoService.translate(
            "assetmanagement.overview.dateInUseHeader",
        );
        headerLookup[nameof<AssetOverviewModel>("dateOutOfUse")] = this.translocoService.translate(
            "assetmanagement.overview.dateOutOfUseHeader",
        );
        headerLookup[nameof<AssetOverviewModel>("thirdPartyName")] =
            this.translocoService.translate("assetmanagement.overview.thirdPartyHeader");

        headerLookup[nameof<AssetOverviewModel>("contractName")] = this.translocoService.translate(
            "assetmanagement.overview.contractHeader",
        );
        return headerLookup;
    }
}
